import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { formatNumber } from '../lib/utils';
import * as api from '../lib/api';

function isoToDisplayDate(iso) {
  if (!iso) return '';
  const d = String(iso).slice(0, 10);
  const [y, m, day] = d.split('-');
  if (!day) return iso;
  return `${day}/${m}/${y}`;
}

function todayIso() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

function vendorKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase();
}

/** Single cost_currency among line items for this vendor, or null if mixed / none. */
function suggestedSettlementCurrency(deal, vk) {
  const curs = new Set();
  for (const it of deal?.line_items || []) {
    if (vendorKey(it.vendor) !== vk) continue;
    const c = String(it.cost_currency || 'CHF').toUpperCase();
    if (c === 'CHF' || c === 'EUR' || c === 'USD') curs.add(c);
  }
  if (curs.size === 1) return [...curs][0];
  return 'USD';
}

export function DealVendorPaymentsTab({ deal, onDealReload }) {
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showChfReference, setShowChfReference] = useState(false);
  const [lockBusy, setLockBusy] = useState(null);
  const [lockDraft, setLockDraft] = useState({});

  const settlementMap = deal?.vendor_settlement_currencies || {};

  const lineVendorChoices = useMemo(() => {
    const seen = new Set();
    const names = [];
    for (const it of deal?.line_items || []) {
      const v = (it.vendor || '').trim();
      if (!v) continue;
      const k = v.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      names.push(v);
    }
    names.sort((a, b) => a.localeCompare(b));
    return names;
  }, [deal?.line_items]);

  const selectOptions = useMemo(() => {
    const seen = new Set();
    const names = [];
    const add = (raw) => {
      const v = (raw || '').trim();
      if (!v) return;
      const k = v.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      names.push(v);
    };
    for (const it of deal?.line_items || []) add(it.vendor);
    for (const row of summary?.vendors || []) add(row.vendor_name);
    names.sort((a, b) => a.localeCompare(b));
    return names;
  }, [deal?.line_items, summary?.vendors]);

  const [form, setForm] = useState({
    vendor_name: '',
    amount_input: '',
    payment_date: todayIso(),
    note: '',
  });

  useEffect(() => {
    if (!deal?.id) return;
    setLockDraft({});
    setForm({
      vendor_name: lineVendorChoices[0] || '',
      amount_input: '',
      payment_date: todayIso(),
      note: '',
    });
  }, [deal?.id, lineVendorChoices]);

  const refresh = useCallback(async () => {
    if (!deal?.id) return;
    setLoadError(null);
    try {
      const res = await api.getDealVendorPayments(deal.id);
      setSummary(res.data);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e?.message || 'Failed to load');
    }
  }, [deal?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const reloadDeal = useCallback(async () => {
    if (onDealReload) await onDealReload();
  }, [onDealReload]);

  const lockSettlement = async (vendorName, currency) => {
    if (!deal?.id) return;
    const vk = vendorKey(vendorName);
    setLockBusy(vk);
    setLoadError(null);
    try {
      await api.patchDeal(deal.id, { vendor_settlement_currencies: { [vk]: currency } });
      await reloadDeal();
      await refresh();
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e?.message || 'Could not lock currency');
    } finally {
      setLockBusy(null);
    }
  };

  const addPayment = async (e) => {
    e.preventDefault();
    if (!deal?.id) return;
    const vk = vendorKey(form.vendor_name);
    const settled = settlementMap[vk];
    if (!settled) {
      setLoadError('Lock a settlement currency for this vendor before logging payments.');
      return;
    }
    const amt = Number(String(form.amount_input).replace(/'/g, '').replace(',', '.'));
    if (!form.vendor_name?.trim() || !Number.isFinite(amt) || amt <= 0) return;
    setBusy(true);
    setLoadError(null);
    try {
      await api.addDealVendorPayment(deal.id, {
        vendor_name: form.vendor_name.trim(),
        amount: amt,
        payment_date: form.payment_date,
        note: (form.note || '').trim(),
      });
      setForm((f) => ({
        ...f,
        amount_input: '',
        note: '',
        payment_date: todayIso(),
      }));
      await refresh();
    } catch (err) {
      setLoadError(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const removePayment = async (paymentId) => {
    if (!deal?.id) return;
    setBusy(true);
    try {
      await api.deleteDealVendorPayment(deal.id, paymentId);
      await refresh();
    } catch (err) {
      setLoadError(err?.response?.data?.detail || err?.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const fmtSummary = (v) => {
    if (showChfReference) {
      return {
        total: `${formatNumber(v.total_cost_chf_reporting ?? 0, 2)} CHF`,
        paid: `${formatNumber(v.paid_chf_reporting ?? 0, 2)} CHF`,
        rem: `${formatNumber(v.remaining_chf_reporting ?? 0, 2)} CHF`,
      };
    }
    const cur = v.settlement_currency || deal?.core_currency || 'CHF';
    if (v.settlement_currency && v.total_cost_vendor != null) {
      return {
        total: `${formatNumber(v.total_cost_vendor ?? 0, 2)} ${cur}`,
        paid: `${formatNumber(v.paid_vendor ?? 0, 2)} ${cur}`,
        rem: `${formatNumber(v.remaining_vendor ?? 0, 2)} ${cur}`,
      };
    }
    const cc = v.deal_core_currency || deal?.core_currency || 'CHF';
    return {
      total: `${formatNumber(v.total_cost_core ?? v.total_cost_chf ?? 0, 2)} ${cc}`,
      paid: `${formatNumber(v.paid_chf ?? 0, 2)} ${cc}`,
      rem: `${formatNumber(v.remaining_chf ?? 0, 2)} ${cc}`,
    };
  };

  const fmtPayment = (p) => {
    if (showChfReference) {
      return `${formatNumber(p.amount_chf ?? 0, 2)} CHF`;
    }
    const cur = p.currency || 'CHF';
    const av = p.amount_vendor != null ? p.amount_vendor : p.amount_chf;
    return `${formatNumber(av ?? 0, 2)} ${cur}`;
  };

  if (!deal?.id) {
    return <div className="p-6 text-sm text-gray-500">No deal selected.</div>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto bg-white">
      <div className="border-b border-gray-100 px-4 py-2 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Vendor Payments</h2>
            <p className="mt-1 text-xs text-gray-500">
              Operate in the vendor&apos;s settlement currency per deal (locked once set). CHF is reporting only — use
              the toggle to view CHF equivalents.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-[11px] text-gray-500">View</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => setShowChfReference((x) => !x)}
              data-testid="vendor-payments-chf-toggle"
            >
              {showChfReference ? 'Vendor currency' : 'CHF (reporting)'}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-3 p-3 sm:p-4">
        {loadError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{loadError}</div>
        )}

        {!summary && !loadError && <div className="text-sm text-gray-400">Loading…</div>}

        {summary?.vendors?.length === 0 && (
          <div className="rounded-md border border-dashed border-gray-200 bg-gray-50/50 px-4 py-8 text-center text-sm text-gray-500">
            No vendors on this deal yet. Tag line items with a vendor to see totals here; you can still log payments by
            choosing a name in the form below.
          </div>
        )}

        {summary?.vendors?.map((v) => {
          const vk = vendorKey(v.vendor_name);
          const locked = settlementMap[vk];
          const sug = suggestedSettlementCurrency(deal, vk);
          const lockPick = lockDraft[vk] ?? sug;
          const s = fmtSummary(v);
          return (
            <section
              key={v.vendor_name}
              className="rounded-lg border border-gray-200 bg-white shadow-sm"
              data-testid={`vendor-payment-block-${v.vendor_name}`}
            >
              <div className="border-b border-gray-100 px-3 py-2 sm:px-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">{v.vendor_name}</h3>
                  {!locked ? (
                    <div className="flex flex-wrap items-end gap-2 rounded-md border border-amber-100 bg-amber-50/60 px-2 py-2 text-[11px] text-amber-900">
                      <span className="self-center text-gray-700">Settlement currency (required once):</span>
                      <select
                        className="rounded border border-amber-200 bg-white px-2 py-1 text-[12px]"
                        value={lockPick}
                        onChange={(e) => setLockDraft((d) => ({ ...d, [vk]: e.target.value }))}
                        disabled={!!lockBusy}
                      >
                        <option value="CHF">CHF</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                      </select>
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 px-2 text-[11px]"
                        disabled={!!lockBusy}
                        onClick={() => lockSettlement(v.vendor_name, lockPick)}
                      >
                        {lockBusy === vk ? 'Saving…' : 'Lock'}
                      </Button>
                    </div>
                  ) : (
                    <span className="text-[11px] font-medium text-gray-500">
                      Settlement: <span className="text-gray-800">{locked}</span> (locked)
                    </span>
                  )}
                </div>
                <dl className="mt-1.5 grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-3 sm:gap-2">
                  <div>
                    <dt className="font-medium uppercase tracking-wider text-gray-400">Total cost</dt>
                    <dd className="font-mono text-[13px] font-semibold tabular-nums text-gray-900">{s.total}</dd>
                  </div>
                  <div>
                    <dt className="font-medium uppercase tracking-wider text-gray-400">Paid</dt>
                    <dd className="font-mono text-[13px] font-semibold tabular-nums text-gray-900">{s.paid}</dd>
                  </div>
                  <div>
                    <dt className="font-medium uppercase tracking-wider text-gray-400">Remaining</dt>
                    <dd className="font-mono text-[13px] font-semibold tabular-nums text-gray-900">{s.rem}</dd>
                  </div>
                </dl>
              </div>
              <div className="overflow-x-auto px-2 pb-2 sm:px-3">
                <div className="mt-2 space-y-2 sm:hidden">
                  {(v.payments || []).length === 0 ? (
                    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-center text-xs text-gray-400">
                      No payments logged yet.
                    </div>
                  ) : (
                    v.payments.map((p) => (
                      <article key={`mobile-${p.id}`} className="rounded-md border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-mono text-xs text-gray-500">{isoToDisplayDate(p.payment_date)}</p>
                            <p className="font-mono text-sm font-semibold text-gray-900">{fmtPayment(p)}</p>
                            {p.fx_rate_to_chf != null ? (
                              <p className="mt-0.5 text-[10px] text-gray-400">
                                FX→CHF {p.fx_rate_to_chf} · {formatNumber(p.amount_chf ?? 0, 2)} CHF
                              </p>
                            ) : null}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-400 hover:text-red-600"
                            disabled={busy}
                            onClick={() => removePayment(p.id)}
                            aria-label="Remove payment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="mt-1 text-xs text-gray-600">{p.note || '—'}</p>
                      </article>
                    ))
                  )}
                </div>
                <table className="mt-1.5 hidden w-full min-w-[360px] text-[12px] sm:table">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs font-medium tracking-wide text-gray-500">
                      <th className="py-2 pl-2 pr-2 text-center">Date</th>
                      <th className="py-2 px-2 text-right">Amount</th>
                      <th className="py-2 px-2 text-left">Note</th>
                      <th className="w-10 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {(v.payments || []).length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-2.5 text-center text-xs text-gray-400">
                          No payments logged yet.
                        </td>
                      </tr>
                    ) : (
                      v.payments.map((p) => (
                        <tr key={p.id} className="border-b border-gray-50">
                          <td className="py-1.5 pl-2 pr-2 text-center font-mono tabular-nums text-gray-700">
                            {isoToDisplayDate(p.payment_date)}
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono tabular-nums font-medium text-gray-900">
                            <div>{fmtPayment(p)}</div>
                            {p.fx_rate_to_chf != null && !showChfReference ? (
                              <div className="text-[10px] font-normal text-gray-400">
                                → CHF {formatNumber(p.amount_chf ?? 0, 2)} @ {p.fx_rate_to_chf}
                              </div>
                            ) : null}
                          </td>
                          <td className="py-1.5 px-2 text-gray-600">{p.note || '—'}</td>
                          <td className="py-1.5 pr-2 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-gray-400 hover:text-red-600"
                              disabled={busy}
                              onClick={() => removePayment(p.id)}
                              aria-label="Remove payment"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}

        <form
          onSubmit={addPayment}
          className="rounded-lg border border-gray-200 bg-gray-50/50 p-3 sm:p-4"
          data-testid="vendor-payment-add-form"
        >
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Add payment</h4>
          <p className="mt-1 text-[11px] text-gray-500">
            Amount is always in the vendor&apos;s locked settlement currency for this deal (not CHF).
          </p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
            <label className="flex min-w-[140px] flex-1 flex-col gap-1 text-xs text-gray-600">
              Vendor
              {selectOptions.length > 0 ? (
                <select
                  className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[12px] text-gray-900"
                  value={form.vendor_name}
                  onChange={(e) => setForm((f) => ({ ...f, vendor_name: e.target.value }))}
                  disabled={busy}
                >
                  {selectOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[12px]"
                  value={form.vendor_name}
                  onChange={(e) => setForm((f) => ({ ...f, vendor_name: e.target.value }))}
                  placeholder="Vendor name"
                  disabled={busy}
                />
              )}
            </label>
            <label className="flex min-w-[120px] flex-1 flex-col gap-1 text-xs text-gray-600">
              {`Amount (${settlementMap[vendorKey(form.vendor_name)] || 'lock first'})`}
              <input
                type="text"
                inputMode="decimal"
                className="rounded-md border border-gray-200 bg-white px-2 py-1.5 font-mono text-[12px]"
                value={form.amount_input}
                onChange={(e) => setForm((f) => ({ ...f, amount_input: e.target.value }))}
                placeholder="0"
                disabled={busy || !settlementMap[vendorKey(form.vendor_name)]}
              />
            </label>
            <label className="flex min-w-[140px] flex-1 flex-col gap-1 text-xs text-gray-600">
              Date
              <input
                type="date"
                className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[12px]"
                value={form.payment_date}
                onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))}
                disabled={busy}
              />
            </label>
            <label className="flex min-w-[160px] flex-[2] flex-col gap-1 text-xs text-gray-600">
              Note (optional)
              <input
                type="text"
                className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[12px]"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                disabled={busy}
              />
            </label>
            <Button
              type="submit"
              disabled={busy || !form.vendor_name?.trim() || !settlementMap[vendorKey(form.vendor_name)]}
              className="h-8 px-3 text-xs sm:mb-0"
            >
              Add payment
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
